import * as React from "react";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { searchSerial } from "../api/returnInwardApi";
import { DEFAULT_SERIAL_STATUS, MAX_SERIALS_PER_INWARD } from "../config/returnKinds";
import type { ExcelSerialRow } from "../components/SerialExcel";
import type { ReturnInwardAction, ReturnInwardState, RowPosition } from "../state/returnInwardReducer";
import { buildOrderLineView, findOrderLineBySerial, missingQuantityOf } from "../state/selectors";
import type { ReturnSerialRow } from "../types";
import { normalizeSerial } from "../utils/serial";

/** Lần thêm serial thành công gần nhất, hiện ở dòng trạng thái cạnh ô quét */
export interface LastAddedSerial {
  serial: string;
  itemName: string | null;
  /** Ví dụ "đủ số lượng dòng đơn · đơn còn thiếu 1 serial"; null khi không xác định */
  remainText: string | null;
}

/** Số serial import Excel kiểm tra cùng lúc */
const IMPORT_CONCURRENCY = 4;

/** Mô tả dòng đơn của serial vừa thêm và phần còn thiếu của cả đơn (rows đã gồm serial đó) */
const describeRemain = (state: ReturnInwardState, rows: ReturnSerialRow[], serial: string): string | null => {
  const lines = buildOrderLineView(state.orderLines, rows);
  const line = lines.find((item) => normalizeSerial(item.serial) === normalizeSerial(serial));
  if (!line) return null;
  const missing = (Number(line.primaryQuantity) || 0) - line.scannedQuantity;
  const lineText = missing > 0 ? `dòng đơn còn thiếu ${missing}` : missing < 0 ? `dòng đơn thừa ${-missing}` : "đủ số lượng dòng đơn";
  const orderMissing = missingQuantityOf(lines);
  return `${lineText} · ${orderMissing > 0 ? `đơn còn thiếu ${orderMissing} serial` : "đủ số lượng đơn"}`;
};

/** Dòng serial mới: vị trí mặc định của phiếu, tình trạng mặc định nếu chưa có */
const toNewRow = (data: ReturnSerialRow, position: RowPosition, extra?: Partial<ReturnSerialRow>): ReturnSerialRow => ({
  ...data,
  subInvName: data.invName,
  note: null,
  primaryQuantity: Number(data.primaryQuantity) || 1,
  subQuantity: data.subQuantity ?? 0,
  serialStatus: data.serialStatus || DEFAULT_SERIAL_STATUS,
  attribute11: data.attribute11 ?? null,
  // Vị trí lấy theo kho nhập của phiếu, không theo vị trí tồn cũ của serial
  locatorId: position.id,
  positionCode: position.code,
  positionName: position.name,
  ...extra,
});

/**
 * Quét serial khách trả vào phiếu. Serial phải thuộc đơn đổi trả (backend kiểm tra khi quét và khi lưu).
 * Serial đang chờ phản hồi được giữ trong ref nên quét nhanh hai lần cùng serial bị chặn ngay.
 */
export const useReturnSerialScanner = (
  state: ReturnInwardState,
  dispatch: React.Dispatch<ReturnInwardAction>,
  defaultPosition: RowPosition,
) => {
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const positionRef = React.useRef(defaultPosition);
  positionRef.current = defaultPosition;
  const pendingRef = React.useRef(new Set<string>());
  const [scanning, setScanning] = React.useState(false);
  const [lastAdded, setLastAdded] = React.useState<LastAddedSerial | null>(null);

  const scan = React.useCallback(
    async (rawSerial: string) => {
      const serial = normalizeSerial(rawSerial);
      const { header, rows, orderLines } = stateRef.current;
      if (!serial) return;
      if (!header.sourceCode) {
        NotificationExtension.Fails("Chưa có số chứng từ");
        return;
      }
      if (!header.subInvId) {
        NotificationExtension.Warn("Vui lòng chọn kho nhập trước khi quét serial");
        return;
      }
      if (rows.some((row) => normalizeSerial(row.serial) === serial) || pendingRef.current.has(serial)) {
        NotificationExtension.Fails(`Mã serial ${serial} đã được quét!`);
        return;
      }
      if (rows.length >= MAX_SERIALS_PER_INWARD) {
        NotificationExtension.Warn(`Số lượng serial vượt quá ${MAX_SERIALS_PER_INWARD}, vui lòng kiểm tra lại`);
        return;
      }

      pendingRef.current.add(serial);
      setScanning(true);
      try {
        const res = await searchSerial({
          serial,
          sourceCode: header.sourceCode,
          type: header.sourceType ?? "",
          isNoSerial: findOrderLineBySerial(orderLines, serial)?.isNoSerial ?? null,
        });
        // res undefined: backend báo lỗi (HTTP 500), lớp gọi API đã hiện thông báo
        if (!res) return;
        if (!res.success || !res.data) {
          NotificationExtension.Fails(res.message || `Không tìm thấy serial ${serial}`);
          return;
        }
        if (res.data.warning) {
          NotificationExtension.Fails(res.data.warning);
          return;
        }
        const latest = stateRef.current;
        const row = toNewRow(res.data, positionRef.current);
        const line = findOrderLineBySerial(latest.orderLines, row.serial);
        if (line) {
          const [view] = buildOrderLineView([line], latest.rows);
          if (view.scannedQuantity + (Number(row.primaryQuantity) || 0) > (Number(line.primaryQuantity) || 0)) {
            NotificationExtension.Warn("Sản phẩm vượt quá số lượng cần nhập, vui lòng kiểm tra lại");
            return;
          }
        }
        dispatch({ type: "ADD_ROWS", rows: [row] });
        const added = row.serial || serial;
        setLastAdded({ serial: added, itemName: row.itemName ?? null, remainText: describeRemain(latest, [...latest.rows, row], added) });
      } catch (error) {
        console.error("[ReturnInward] quét serial lỗi", error);
        NotificationExtension.Fails(`Quét serial ${serial} lỗi, vui lòng thử lại.`);
      } finally {
        pendingRef.current.delete(serial);
        setScanning(pendingRef.current.size > 0);
      }
    },
    [dispatch],
  );

  /**
   * Thêm nhiều serial từ tệp Excel (cột Serial, SerialStatus, SerialNote).
   * Mỗi serial kiểm tra bằng cùng API với lúc quét tay (thuộc đơn đổi trả, tồn của serial), vì API tìm danh sách
   * với đổi trả không lọc theo đơn và bỏ qua kiểm tra tồn. Tình trạng không có trong danh mục chuyển thành
   * "Bán bình thường", như trang cũ.
   */
  const addMany = React.useCallback(
    async (excelRows: ExcelSerialRow[], serialStatuses: string[]) => {
      const { header, rows } = stateRef.current;
      if (!header.sourceCode) {
        NotificationExtension.Fails("Chưa có số chứng từ");
        return;
      }
      if (!header.subInvId) {
        NotificationExtension.Warn("Vui lòng chọn kho nhập trước khi import serial");
        return;
      }
      const byKey = new Map<string, ExcelSerialRow>();
      excelRows.forEach((item) => {
        const key = normalizeSerial(item.serial);
        if (key && !byKey.has(key)) byKey.set(key, item);
      });
      if (!byKey.size) {
        NotificationExtension.Fails("Không tìm thấy serial trong tệp Excel.");
        return;
      }
      const scanned = new Set(rows.map((row) => normalizeSerial(row.serial)));
      const wanted = Array.from(byKey.keys()).filter((key) => !scanned.has(key) && !pendingRef.current.has(key));
      if (!wanted.length) {
        NotificationExtension.Warn("Các serial trong tệp đã có trên phiếu.");
        return;
      }
      if (rows.length + wanted.length > MAX_SERIALS_PER_INWARD) {
        NotificationExtension.Warn(`Số lượng serial vượt quá ${MAX_SERIALS_PER_INWARD}, vui lòng kiểm tra lại`);
        return;
      }

      const sourceCode = header.sourceCode;
      const type = header.sourceType ?? "";
      const found = new Map<string, ReturnSerialRow>();
      const failed: string[] = [];
      let invalidStatus = false;
      wanted.forEach((key) => pendingRef.current.add(key));
      setScanning(true);
      try {
        let next = 0;
        const worker = async () => {
          while (next < wanted.length) {
            const key = wanted[next++];
            try {
              const res = await searchSerial({
                serial: key,
                sourceCode,
                type,
                isNoSerial: findOrderLineBySerial(stateRef.current.orderLines, key)?.isNoSerial ?? null,
              });
              if (!res?.success || !res.data || res.data.warning) {
                failed.push(key);
                continue;
              }
              const excel = byKey.get(key);
              const status = excel?.serialStatus ?? "";
              const validStatus = !!status && serialStatuses.includes(status);
              if (status && !validStatus) invalidStatus = true;
              found.set(
                key,
                toNewRow(res.data, positionRef.current, {
                  serialStatus: validStatus ? status : res.data.serialStatus || DEFAULT_SERIAL_STATUS,
                  attribute11: excel?.serialNote || null,
                }),
              );
            } catch (error) {
              console.error("[ReturnInward] kiểm tra serial import lỗi", key, error);
              failed.push(key);
            }
          }
        };
        await Promise.all(Array.from({ length: Math.min(IMPORT_CONCURRENCY, wanted.length) }, worker));

        // Giữ thứ tự dòng như trong tệp
        const newRows = wanted.map((key) => found.get(key)).filter((row): row is ReturnSerialRow => !!row);
        if (newRows.length) {
          dispatch({ type: "ADD_ROWS", rows: newRows });
          setLastAdded({ serial: `${newRows.length} serial`, itemName: null, remainText: null });
          NotificationExtension.Success(`Đã thêm ${newRows.length} serial từ tệp Excel.`);
        }
        if (invalidStatus) {
          NotificationExtension.Warn(
            "Danh sách sản phẩm có các sản phẩm chứa tình trạng serial không hợp lệ. Các sản phẩm này sẽ đổi tình trạng thành bán bình thường.",
          );
        }
        if (failed.length) {
          const ordered = wanted.filter((key) => failed.includes(key));
          NotificationExtension.Warn(
            `Không thêm được ${ordered.length} serial (không thuộc đơn đổi trả hoặc không hợp lệ): ${ordered.join(", ")}`,
          );
        }
      } finally {
        wanted.forEach((key) => pendingRef.current.delete(key));
        setScanning(pendingRef.current.size > 0);
      }
    },
    [dispatch],
  );

  return { scan, addMany, scanning, lastAdded };
};
